import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Star, Copy, RefreshCw, ArrowLeft, Sparkles, ChevronDown, ChevronUp, Check,
  Circle, CheckCircle2, Loader2, Mail, Send,
} from 'lucide-react';

const MAX_API_CALLS = 10;
const PLATFORM_CHAR_LIMITS = { google: 4096, trustpilot: 10000, provenexpert: 4000 };

// Reihenfolge = Anzeigereihenfolge (Google zuerst — Feedback 04.09., 76:51)
const ALL_PLATFORMS = [
  { key: 'google', label: 'Google', icon: (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>) },
  { key: 'provenexpert', label: 'ProvenExpert', icon: (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" stroke="#E74C3C" strokeWidth="2"/>
      <path d="M12 6l1.5 3.5H17l-2.8 2.2 1 3.5L12 13l-3.2 2.2 1-3.5L7 9.5h3.5z" fill="#E74C3C"/>
    </svg>) },
  { key: 'trustpilot', label: 'Trustpilot', icon: (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#00B67A"/>
      <path d="M12 6l1.5 3.5H17l-2.8 2.2 1 3.5L12 13l-3.2 2.2 1-3.5L7 9.5h3.5z" fill="white"/>
    </svg>) },
];

const QUICK_PILLS = [
  { label: 'Kürzer', instruction: 'Mache den Text kürzer und prägnanter, maximal 2 Sätze.' },
  { label: 'Länger', instruction: 'Erweitere den Text mit mehr Details und konkreten Beispielen.' },
  { label: 'Euphorischer', instruction: 'Formuliere den Text begeisterter und enthusiastischer.', positiveOnly: true },
  { label: 'Sachlicher', instruction: 'Formuliere den Text nüchterner und sachlicher.' },
  { label: 'Lockerer', instruction: 'Formuliere den Text informeller, persönlicher und herzlicher.' },
];
const TOPIC_PILLS = [
  { label: 'Kommunikation', instruction: 'Hebe besonders die gute Kommunikation hervor.' },
  { label: 'Service', instruction: 'Betone den ausgezeichneten Service.' },
  { label: 'Schnelligkeit', instruction: 'Hebe die schnelle Reaktionszeit und Bearbeitung hervor.' },
  { label: 'Freundlichkeit', instruction: 'Betone die Freundlichkeit und Hilfsbereitschaft.' },
  { label: 'Transparenz', instruction: 'Erwähne die transparente Arbeitsweise und Abrechnung.' },
  { label: 'Zuverlässigkeit', instruction: 'Betone die Verlässlichkeit und Pünktlichkeit.' },
];

const addUtm = (url) => {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}utm_source=immoverita&utm_medium=review_funnel`;
};
const fmt = (n) => Number(n).toFixed(1).replace('.', ',');

const HEADLINES = {
  positive:  { title: 'Toll, dass Sie so zufrieden sind!',       sub: 'Übertragen Sie Ihre Bewertung mit einem Klick auf die Plattform Ihrer Wahl.' },
  converted: { title: 'Danke für Ihr differenziertes Feedback!', sub: (avg) => `Im Detail liegt Ihre Bewertung bei ${fmt(avg)} von 5 — übertragen Sie sie gern öffentlich.` },
  critical:  { title: 'Ihre Bewertung veröffentlichen',          sub: 'Ihr Text aus dem vorherigen Schritt wurde übernommen. Sie können ihn noch anpassen.' },
};

// Auf Modul-Ebene definiert — als innere Komponente würde React sie bei jedem Render neu
// mounten und das Input verlöre nach jedem Tastendruck den Fokus (siehe CLAUDE.md, SectionCard-Bug).
function EmailBlock({ email, setEmail, contactWish, setContactWish }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-medium mb-1.5 block">E-Mail-Adresse <span className="text-muted-foreground font-normal">(optional)</span></label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="email" value={email || ''} onChange={(e) => setEmail && setEmail(e.target.value)}
                 placeholder="ihre@email.de" className="pl-10 bg-background" data-testid="finish-email-input" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Wir senden Ihnen nur einen Link, mit dem Sie Ihre Bewertung bestätigen, anpassen oder löschen können — kein Newsletter.
          Mehr in der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Datenschutzerklärung</a>.
        </p>
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" checked={contactWish} onChange={(e) => setContactWish(e.target.checked)} className="mt-0.5 accent-primary" />
        <span className="text-sm">Die Hausverwaltung darf mich bei Rückfragen kontaktieren</span>
      </label>
    </div>
  );
}

/*
 * Positiv-Abschluss in ZWEI Schritten (Feedback 04.09., 76:51 / #36):
 *   1. Textvorschlag anpassen   2. Bewertung an Plattform übertragen lassen
 * Es erscheinen nur Plattformen, für die die HV einen Link hinterlegt hat.
 * Varianten: positive (Impuls 4–5), converted (nach Verfeinerung ≥ 3,5), critical ("Trotzdem absenden").
 */
export default function PositiveStep({
  selectedRating, refinedAvg, variant = 'positive', emailFirst = false,
  suggestion, setSuggestion, onCopy, onRegenerate, onRefineSuggestion, onGenerateTitle, regenerating,
  onPlatformChoice, onBack, targetPlatforms, platformUrls,
  email, setEmail, onFinish, finishing,
}) {
  const availablePlatforms = ALL_PLATFORMS
    .filter(p => !!platformUrls?.[p.key])
    .filter(p => !targetPlatforms || targetPlatforms.includes(p.key));

  const [apiCallCount, setApiCallCount] = useState(0);
  const [showTopicPills, setShowTopicPills] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [reviewTitle, setReviewTitle] = useState('');
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [titleCopied, setTitleCopied] = useState(false);
  const [textCopied, setTextCopied] = useState(false);
  const [refining, setRefining] = useState(false);
  const [clickedPlatforms, setClickedPlatforms] = useState({});
  const [contactWish, setContactWish] = useState(false);

  const anyClicked = Object.values(clickedPlatforms).some(Boolean);
  const canCallApi = apiCallCount < MAX_API_CALLS;
  const apiCallsLeft = MAX_API_CALLS - apiCallCount;
  const isBusy = regenerating || refining || generatingTitle;
  const headline = HEADLINES[variant] || HEADLINES.positive;
  const starsValue = variant === 'positive' ? (selectedRating || 5) : (refinedAvg || selectedRating || 0);

  const overflow = (() => {
    const limits = availablePlatforms.map(p => PLATFORM_CHAR_LIMITS[p.key]).filter(Boolean);
    if (!limits.length || !suggestion) return null;
    const min = Math.min(...limits);
    return suggestion.length > min ? suggestion.length - min : null;
  })();

  const callRegenerate = async () => { if (!canCallApi || isBusy) return; setApiCallCount(c => c + 1); await onRegenerate(); };
  const callRefine = async (instruction) => {
    if (!canCallApi || isBusy) return;
    setRefining(true); setApiCallCount(c => c + 1);
    try { await onRefineSuggestion(instruction, suggestion); } finally { setRefining(false); }
  };
  const applyTopics = async () => {
    if (!selectedTopics.length || !canCallApi || isBusy) return;
    await callRefine(selectedTopics.map(t => TOPIC_PILLS.find(p => p.label === t)?.instruction).filter(Boolean).join(' Außerdem: '));
    setSelectedTopics([]);
  };
  const ensureTitle = async () => {
    if (reviewTitle || !onGenerateTitle) return reviewTitle;
    setGeneratingTitle(true); setApiCallCount(c => c + 1);
    try { const t = await onGenerateTitle(suggestion); setReviewTitle(t || ''); return t; } finally { setGeneratingTitle(false); }
  };
  const handleCopyText = async () => { await onCopy(suggestion); setTextCopied(true); setTimeout(() => setTextCopied(false), 2500); };
  const handleCopyTitle = async () => { if (!reviewTitle) return; await navigator.clipboard.writeText(reviewTitle); setTitleCopied(true); setTimeout(() => setTitleCopied(false), 2500); };

  const handlePlatformClick = async (key) => {
    // window.open MUSS die erste, synchrone Aktion im Click-Handler sein — sonst blockt Safari/iOS das Popup.
    const url = addUtm(platformUrls?.[key]);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    setClickedPlatforms(prev => ({ ...prev, [key]: true }));
    try { await onCopy(suggestion); } catch { /* Clipboard kann fehlen (iOS ohne Geste, http) — kein Blocker */ }
    if (key === 'trustpilot') ensureTitle();
    try { await onPlatformChoice(key); } catch {}
  };

  return (
    <motion.div key={`positive-${variant}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
      <Card className="card-shadow">
        <CardContent className="pt-6 pb-6 space-y-5">

          {/* Kopf */}
          <div className="text-center space-y-2 pb-1">
            <div className="flex gap-1 justify-center">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`w-7 h-7 ${s <= Math.round(starsValue) ? 'fill-yellow-400 text-yellow-400' : 'fill-muted text-muted'}`} />
              ))}
            </div>
            <h2 className="text-lg font-semibold leading-snug">{headline.title}</h2>
            <p className="text-sm text-muted-foreground">
              {typeof headline.sub === 'function' ? headline.sub(refinedAvg) : headline.sub}
            </p>
          </div>

          {/* Kritischer Pfad: E-Mail zuerst (Feedback 62:15) */}
          {emailFirst && (
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <EmailBlock email={email} setEmail={setEmail} contactWish={contactWish} setContactWish={setContactWish} />
            </div>
          )}

          {/* Schritt 1 — Textvorschlag */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">Schritt 1 — Textvorschlag anpassen</label>
              {apiCallsLeft <= 3 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {apiCallsLeft} KI-{apiCallsLeft === 1 ? 'Anpassung' : 'Anpassungen'} übrig
                </span>
              )}
            </div>
            {variant === 'critical' && (
              <p className="text-[11px] text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Aus dem vorherigen Schritt übernommen
              </p>
            )}
            <div className="relative">
              <Textarea value={suggestion} onChange={(e) => setSuggestion(e.target.value)}
                        className="pr-20 text-sm leading-relaxed resize-none min-h-[140px]"
                        onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                        data-testid="ai-suggestion-textarea" />
              <div className="absolute top-2 right-2 flex gap-1">
                <Button size="icon" variant="ghost" onClick={handleCopyText} title="Text kopieren" data-testid="ai-suggestion-1-copy-button">
                  {textCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button size="icon" variant="ghost" onClick={callRegenerate} disabled={!canCallApi || isBusy} title="Neuen Vorschlag generieren" data-testid="ai-suggestion-regenerate-button">
                  <RefreshCw className={`w-4 h-4 ${(regenerating && !refining) ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            {overflow !== null && (
              <p className="text-xs text-red-600 mt-1">Text ist {overflow} Zeichen zu lang für die gewählten Plattformen. Bitte kürzen.</p>
            )}

            <div className="flex flex-wrap gap-2 mt-2">
              {QUICK_PILLS.filter(p => !p.positiveOnly || variant !== 'critical').map(pill => (
                <button key={pill.label} onClick={() => callRefine(pill.instruction)} disabled={!canCallApi || isBusy}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!canCallApi || isBusy ? 'opacity-40 cursor-not-allowed bg-muted text-muted-foreground border-border' : 'bg-background hover:bg-primary/5 hover:border-primary/40 text-foreground border-border cursor-pointer'}`}>
                  {refining && <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin mr-1 align-middle" />}
                  {pill.label}
                </button>
              ))}
              {variant !== 'critical' && (
                <button onClick={() => setShowTopicPills(v => !v)}
                        className="text-xs px-3 py-1.5 rounded-full border border-dashed border-border bg-background hover:bg-primary/5 hover:border-primary/40 transition-colors cursor-pointer flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Was hat gut gefallen?
                  {showTopicPills ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>
            <AnimatePresence>
              {showTopicPills && variant !== 'critical' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="flex flex-wrap gap-2 pt-2 pl-1">
                    {TOPIC_PILLS.map(pill => {
                      const active = selectedTopics.includes(pill.label);
                      return (
                        <button key={pill.label} onClick={() => setSelectedTopics(prev => active ? prev.filter(l => l !== pill.label) : [...prev, pill.label])}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-primary/5 hover:bg-primary/10 text-primary border-primary/30'}`}>
                          {active && <Check className="w-3 h-3 inline mr-1" />}{pill.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTopics.length > 0 && (
                    <Button size="sm" onClick={applyTopics} disabled={!canCallApi || isBusy} className="mt-2 text-xs">
                      {refining
                        ? <><span className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin mr-1.5" />Anpassen…</>
                        : <><Sparkles className="w-3 h-3 mr-1.5" />{selectedTopics.length === 1 ? '1 Thema' : `${selectedTopics.length} Themen`} einarbeiten</>}
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Schritt 2 — Übertragen */}
          <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-4 space-y-3">
            <label className="text-sm font-semibold block">Schritt 2 — Bewertung an Plattform übertragen lassen</label>
            <p className="text-xs text-muted-foreground">Ihr Text wird beim Klick automatisch kopiert — dort nur noch einfügen und absenden.</p>

            {availablePlatforms.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                Für diese Hausverwaltung ist noch keine Bewertungsplattform hinterlegt.
              </p>
            ) : (
              <div className="space-y-2">
                {availablePlatforms.map(platform => {
                  const clicked = clickedPlatforms[platform.key];
                  return (
                    <div key={platform.key}>
                      <button onClick={() => handlePlatformClick(platform.key)}
                              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${clicked ? 'border-green-400 bg-green-50' : 'border-border bg-background hover:border-primary/40 hover:bg-primary/5'}`}
                              data-testid={`step3-platform-${platform.key}`}>
                        {platform.icon}
                        <span className="font-medium text-sm flex-1">Bei {platform.label} bewerten</span>
                        {clicked ? <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" /> : <Circle className="w-5 h-5 text-muted-foreground ml-auto" />}
                      </button>
                      {/* Trustpilot verlangt einen Titel — direkt unter dem Button, erst nach Klick (#34) */}
                      {platform.key === 'trustpilot' && clicked && (
                        <div className="mt-1.5 ml-1 rounded-lg border border-green-200 bg-green-50/60 p-2.5">
                          <p className="text-[11px] font-medium text-green-800 mb-1">Trustpilot verlangt eine Überschrift — hier ist sie:</p>
                          {generatingTitle ? (
                            <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> wird erstellt…</p>
                          ) : reviewTitle ? (
                            <div className="flex items-center gap-2">
                              <Input value={reviewTitle} onChange={(e) => setReviewTitle(e.target.value)} className="h-8 text-sm bg-background" />
                              <Button size="icon" variant="ghost" onClick={handleCopyTitle} className="h-8 w-8 shrink-0" title="Titel kopieren">
                                {titleCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={ensureTitle} disabled={!canCallApi} className="h-7 text-xs">Überschrift erstellen</Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Abschluss */}
          <AnimatePresence>
            {(anyClicked || emailFirst || availablePlatforms.length === 0) && (
              <motion.div key="completion" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                          className={`rounded-xl border-2 p-4 space-y-4 ${anyClicked ? 'border-green-300 bg-green-50' : 'border-border bg-muted/30'}`}>
                {anyClicked && (
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-green-600 shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">Super, vielen Dank!</p>
                      <p className="text-xs text-green-700">Ihre Bewertung hilft anderen Eigentümern &amp; Mietern.</p>
                    </div>
                  </div>
                )}
                {!emailFirst && <EmailBlock email={email} setEmail={setEmail} contactWish={contactWish} setContactWish={setContactWish} />}
                <Button onClick={() => onFinish && onFinish(contactWish)} disabled={finishing} className="w-full" data-testid="finish-submit-button">
                  {finishing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Wird gespeichert…</> : <><Send className="w-4 h-4 mr-2" />Abschließen</>}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {onBack && (
            <div className="pt-1 border-t">
              <Button variant="ghost" size="sm" onClick={onBack} className="w-full text-muted-foreground">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {variant === 'positive' ? 'Bewertung ändern' : 'Zurück'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
